import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import aranaLogo from '../arana-bros.svg';
import { FadeUp, useGalleries } from '../hooks';
import ContactForm from '../components/ContactForm';
import '../styles.css';

const faqs = [
  { q: "What types of projects does Arana Brothers Construction specialize in?", a: "We specialize in renovation and remodel, structural work, and custom functional art installations — for both residential and commercial clients across the Bay Area and North Florida." },
  { q: "How much experience do the Arana brothers have?", a: "Combined, we bring over 75 years of hands-on experience in the construction industry, with deep expertise in structural assessment, custom installations, and high-finish renovation work." },
  { q: "Do you work on both residential and commercial properties?", a: "Yes. Our portfolio spans private homes, commercial spaces, and mixed-use buildings. We apply the same standard of craft regardless of project scale." },
  { q: "What areas do you serve?", a: "We operate in two regions: the San Francisco Bay Area — including SF, Marin, Oakland, and the East Bay — and North Florida." },
  { q: "What is 'Functional Art' in construction?", a: "Custom-built elements that are both structurally sound and visually deliberate — built-in cabinetry, statement staircases, architectural millwork, and one-of-a-kind installations designed to last." },
  { q: "How do I start a project?", a: "Use the contact form below or click 'Start a Project'. We'll respond within one business day to discuss your scope, timeline, and budget." },
];

function imgSrc(img) {
  return `${process.env.PUBLIC_URL}${img.src}`;
}

function imgSrcSet(img) {
  if (!img?.srcset) return undefined;
  return img.srcset.split(', ')
    .map(part => {
      const i = part.lastIndexOf(' ');
      return `${process.env.PUBLIC_URL}${part.slice(0, i)} ${part.slice(i + 1)}`;
    })
    .join(', ');
}

const categoryLinks = {
  repairs_renovation: { label: 'Repairs & Renovations', path: '/services/repairs' },
  custom_installations: { label: 'Custom Installations', path: '/services/functional-art' },
  structural_work: { label: 'Structural Work', path: '/services/structural' },
};

export default function HomePage() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const data = useGalleries();
  const heroImages = data
    .map(gallery => gallery.images?.[0] || gallery.heroImage)
    .filter(Boolean);

  useEffect(() => { setTimeout(() => setHeroVisible(true), 80); }, []);

  useEffect(() => {
    if (heroImages.length < 2) {
      setHeroImageIndex(0);
      return undefined;
    }

    const interval = setInterval(() => {
      setHeroImageIndex(current => (current + 1) % heroImages.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const previewGalleries = data.slice(-7).reverse();

  return (
    <>
      <style>{homeStyles}</style>

      {/* HERO */}
      <section className="hero">
        <div className="hero-carousel" aria-hidden="true">
          {heroImages.map((image, index) => (
            <img
              key={`${image.src}-${index}`}
              src={imgSrc(image)}
              srcSet={imgSrcSet(image)}
              sizes="100vw"
              alt=""
              className={index === heroImageIndex ? 'active' : ''}
            />
          ))}
        </div>
        <div className="hero-left">
          <div className={`fade-up${heroVisible ? ' visible' : ''}`} style={{ transitionDelay: '100ms' }}>
            <img src={aranaLogo} alt="Arana Brothers Construction" className="hero-svg-logo" />
          </div>
          <div className={`fade-up${heroVisible ? ' visible' : ''}`} style={{ transitionDelay: '300ms' }}>
            <div className="hero-established">Bay Area · North Florida</div>
          </div>
        </div>

        <div className="hero-right">
          <div className={`fade-up${heroVisible ? ' visible' : ''}`} style={{ transitionDelay: '380ms' }}>
            <h1 className="hero-statement">Craftsmanship<br /><em>Built to</em><br />Last</h1>
          </div>
          <div className={`fade-up${heroVisible ? ' visible' : ''}`} style={{ transitionDelay: '500ms' }}>
            <p className="hero-body">Renovation, structural work, and custom installation — residential and commercial. Two brothers, two coasts, one standard of work.</p>
          </div>
          <div className={`fade-up${heroVisible ? ' visible' : ''}`} style={{ transitionDelay: '620ms' }}>
            <div className="hero-ctas">
              <Link to="/#contact" className="btn-primary">Start a Project</Link>
              <Link to="/#gallery" className="btn-secondary">Visit Our Portfolio</Link>
            </div>
          </div>
        </div>

      </section>

      {/* SERVICES */}
      <section className="services" id="services">
        <FadeUp>
          <div className="section-eyebrow">What We Do</div>
          <h2 className="section-title">Our Services</h2>
          <div className="section-rule" />
        </FadeUp>
        <FadeUp delay={100}>
          <div className="services-grid">
            {[
              { slug: 'repairs', num: '01', icon: '◇', title: 'Repairs & Renovations', desc: 'Full-scope remodels and precision repairs for residential and commercial properties — restoring what exists, elevating what comes next.' },
              { slug: 'functional-art', num: '02', icon: '◇', title: 'Custom Installations & Functional Art', desc: 'Built-ins, architectural millwork, statement staircases, and one-of-a-kind elements crafted to live at the intersection of function and design.' },
              { slug: 'structural', num: '03', icon: '◇', title: 'Structural Work', desc: 'Foundations. Framing. Load-bearing systems. The structure beneath everything else — assessed, repaired, and built to last.' },
            ].map(s => (
              <div key={s.slug} className="service-card">
                <div className="service-number">{s.num}</div>
                <div className="service-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <Link to={`/services/${s.slug}`} className="service-link">Learn More →</Link>
              </div>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="gallery-preview" id="gallery">
        <FadeUp>
          <div className="section-eyebrow">Portfolio</div>
          <h2 className="section-title">Recent Work</h2>
          <div className="section-rule" />
        </FadeUp>
        <FadeUp delay={150}>
          <div className="gallery-grid">
            {previewGalleries.length > 0

              /**
               * Gallery tiles — maps the first 4 galleries from galleries.json.
               *
               * Each gallery object contains:
               *   gallery.slug      — URL-safe folder name, used to build the route (e.g. "san-francisco-kitchen")
               *   gallery.type      — Human-readable project type displayed as the tile heading (e.g. "Kitchen")
               *   gallery.city      — City name displayed as the tile subheading (e.g. "San Francisco")
               *   gallery.title     — Full title used for the img alt text (e.g. "San Francisco Kitchen")
               *   gallery.heroImage — Image object { src, srcset } for the tile background photo
               *
               * If galleries.json has not loaded yet, four numbered placeholder
               * tiles are rendered to preserve the grid layout.
               */
              ? previewGalleries.map((gallery) => (
                <div
                  key={gallery.slug}
                   className="gallery-tile"
                >
                  {gallery.heroImage
                     ? <img
                        src={imgSrc(gallery.heroImage)}
                        srcSet={imgSrcSet(gallery.heroImage)}
                        sizes="(max-width: 600px) 100vw, (max-width: 900px) 50vw, 33vw"
                        alt={gallery.title}
                        className="gallery-tile-img"
                        />
                     : <div className="img-placeholder" />
                  }
                  <Link to={`/gallery/${gallery.slug}`} className="gallery-tile-overlay" aria-label={`View ${gallery.title}`} />
                  <div className="gallery-tile-label">
                     <h3><Link to={`/gallery/${gallery.slug}`}>{gallery.type}</Link></h3>
                     <span>{gallery.city}</span>
                     {categoryLinks[gallery.category] && (
                       <Link to={categoryLinks[gallery.category].path} className="gallery-category-link">
                         {categoryLinks[gallery.category].label}
                       </Link>
                      )}
                  </div>
                  <div className="gallery-tile-link">→</div>
                 </div>
              ))

              /* Fallback — renders 4 placeholder tiles while gallery data loads */
              : [1, 2, 3, 4].map((placeholderNumber) => (
                <div key={placeholderNumber} className="gallery-tile">
                  <div className="img-placeholder" />
                  <div className="gallery-tile-label"><h3>Project {placeholderNumber}</h3></div>
                </div>
              ))
            }
          </div>
        </FadeUp>
      </section>


      {/* FAQ */}
      <section className="faq-section" id="faq">
        <FadeUp>
          <div className="section-eyebrow">Common Questions</div>
          <h2 className="section-title">What People Ask</h2>
          <div className="section-rule" />
        </FadeUp>
        <FadeUp delay={100}>
          <div className="faq-grid">
            {faqs.map((f, i) => (
              <div key={i} className="faq-item">
                <div className="faq-q">{f.q}</div>
                <div className="faq-a">{f.a}</div>
              </div>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* TEAM */}
      <section className="team" id="team">
        <FadeUp>
          <div className="section-eyebrow">Who We Are</div>
          <h2 className="section-title">The Arana Brothers</h2>
          <div className="section-rule" />
        </FadeUp>
        <FadeUp delay={150}>
          <div className="team-inner">
            <div className="team-portraits">
              <div className="portrait-circle">
                <img
                  src={`${process.env.PUBLIC_URL}/images/team/5538EA67-0BED-442D-862A-FE87145D2771_1_201_a.webp`}
                  alt="Arana Brothers owner"
                  loading="lazy"
                />
              </div>
              <div className="portrait-circle">
                <img
                  src={`${process.env.PUBLIC_URL}/images/team/AB038DC2-38AA-4D1B-A966-86EABECE506E_1_201_a.webp`}
                  alt="Arana Brothers owner"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="team-bio">
              <h3>Two brothers.<br />One standard of work.</h3>
              <p>We are two brothers with over 75 years of combined experience in the construction industry — primarily in renovation, remodel, repair, structural assessment, and custom installation, working in both commercial and residential arenas.</p>
              <p>Based on opposite coasts — the San Francisco Bay Area and North Florida — we've spent our careers building the kind of work that doesn't require a follow-up call.</p>
              <div className="team-stats">
                <div className="stat-item"><div className="stat-number">75+</div><div className="stat-label">Years Combined</div></div>
                <div className="stat-item"><div className="stat-number">2</div><div className="stat-label">Coasts</div></div>
                <div className="stat-item"><div className="stat-number">1</div><div className="stat-label">Standard</div></div>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* CONTACT */}
      <section className="contact-section" id="contact">
        <div className="contact-inner">
          <FadeUp>
            <div className="contact-left">
              <div className="section-eyebrow" style={{ color: 'var(--aqua-light)' }}>Get In Touch</div>
              <h2 className="section-title" style={{ color: 'white' }}>Start a Project</h2>
              <div className="section-rule" style={{ background: 'var(--gold)' }} />
              <p>Tell us what you are working with.</p>
              <div className="contact-info-item"><div className="contact-info-icon">◈</div><div className="contact-info-text"><strong>Bay Area</strong><br />San Francisco-Marin-Sonoma</div></div>
              <div className="contact-info-item"><div className="contact-info-icon">◈</div><div className="contact-info-text"><strong>North Florida</strong><br />Serving communities across the region</div></div>
              <div className="contact-info-item"><div className="contact-info-icon">✉</div><div className="contact-info-text"><strong>Email</strong><br />contact@aranabros.com</div></div>
            </div>
          </FadeUp>
          <FadeUp delay={150}>
            <ContactForm />
          </FadeUp>
        </div>
      </section>
    </>
  );
}

const homeStyles = `
.hero {
  min-height: 100vh; display: grid; grid-template-columns: 1fr 1fr;
  padding-top: 72px; position: relative; overflow: hidden;
  background: var(--aqua-dark);
}
.hero-carousel { position: absolute; inset: 0; z-index: 0; background: var(--aqua-dark); }
.hero-carousel::after { content: ''; position: absolute; inset: 0; background: rgba(26,42,46,0.28); }
.hero-carousel img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0; filter: brightness(0.72); transition: opacity 1.2s ease; }
.hero-carousel img.active { opacity: 1; }
.hero-left {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 5rem 4rem 5rem 5rem; position: relative; z-index: 1;
  background: rgba(26,42,46,0.68); border-right: none; border-radius: 0;
  backdrop-filter: blur(2px);
}
.hero-svg-logo {
  width: 100%; max-width: 529px; height: auto;
  display: block; margin-bottom: 2rem;
}
.hero-established {
  font-family: 'Barlow', sans-serif; font-size: 0.7rem;
  letter-spacing: 0.15em; color: white; text-transform: uppercase;
}
.hero-right {
  display: flex; flex-direction: column; justify-content: center;
  padding: 5rem 5rem 5rem 4rem; position: relative; z-index: 1;
  background: rgba(26,42,46,0.68);
}
.hero-eyebrow {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.7rem; font-weight: 700;
  letter-spacing: 0.25em; text-transform: uppercase;
  color: var(--gold); margin-bottom: 1.5rem;
}
.hero-statement {
  font-family: 'Cormorant Garamond', serif;
  font-size: 3rem; font-weight: 300;
  line-height: 1.2; color: white; margin-bottom: 1.5rem;
}
.hero-statement em { font-style: normal; color: var(--gold); }
.hero-body {
  font-size: 0.95rem; font-weight: 300;
  line-height: 1.75; color: rgba(255,255,255,0.75);
  max-width: 420px; margin-bottom: 2.5rem;
}
.hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }
.hero-coasts {
  position: absolute; bottom: 2.5rem; left: 5rem;
  display: flex; gap: 2rem; z-index: 2;
}
.coast-tag {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.65rem; font-weight: 600;
  letter-spacing: 0.2em; text-transform: uppercase;
  color: rgba(255,255,255,0.6); display: flex; align-items: center; gap: 0.5rem;
}
.coast-tag::before {
  content: ''; display: block; width: 20px; height: 1px; background: var(--gold);
}
.gallery-preview { background: var(--off-white); padding: 6rem 5rem; }
.gallery-grid {
  column-count: 3; column-gap: 1.5rem; margin-top: 3rem;
}
.gallery-tile {
  position: relative; overflow: hidden;
  cursor: pointer; background: var(--aqua-dark);
  text-decoration: none; display: inline-block; width: 100%;
  margin: 0 0 1.5rem; break-inside: avoid;
  border-radius: 10px;
  border: 3px solid white;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}
.gallery-tile .img-placeholder {
  width: 100%; aspect-ratio: 4 / 3; background: linear-gradient(135deg, var(--aqua-dark), var(--aqua-light));
  opacity: 0.7; transition: transform 0.5s ease;
}
.gallery-tile-overlay { position: absolute; inset: 0; z-index: 1; }
.gallery-tile:hover .img-placeholder { transform: scale(1.04); }
.gallery-tile-img { width: 100%; height: auto; display: block; filter: brightness(0.75); transition: transform 0.5s ease, filter 0.3s; }
.gallery-tile:hover .gallery-tile-img { transform: scale(1.04); filter: brightness(0.6); }
.gallery-tile-label {
  position: absolute; bottom: 0; left: 0; right: 0;
  padding: 2rem 1.5rem 1.25rem;
  background: linear-gradient(to top, rgba(26,42,46,0.85), transparent);
  z-index: 2; pointer-events: none;
}
.gallery-tile-label h3 {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.4rem; font-weight: 400; color: white;
}
.gallery-tile-label h3 a { color: inherit; text-decoration: none; pointer-events: auto; }
.gallery-tile-label span {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.62rem; font-weight: 600;
  letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--gold); display: block; margin-top: 0.25rem;
}
.gallery-category-link {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.62rem; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--gold); display: block; margin-top: 0.35rem;
  text-decoration: underline; text-underline-offset: 2px;
  transition: color 0.2s;
  pointer-events: auto;
}
.gallery-category-link:hover { color: var(--aqua-dark); }
.gallery-tile-link {
  position: absolute; top: 1rem; right: 1rem;
  width: 32px; height: 32px;
  border: 1px solid rgba(255,255,255,0.4);
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 1rem; z-index: 3; pointer-events: none;
  opacity: 0; transition: opacity 0.3s;
}
.gallery-tile:hover .gallery-tile-link { opacity: 1; }
.services { background: white; padding: 6rem 5rem; }
.services-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 2px; margin-top: 3rem; border: 2px solid rgba(141,189,189,0.2); }
.service-card {
  padding: 2.5rem; border-right: 2px solid rgba(141,189,189,0.2);
  transition: background 0.3s; position: relative; overflow: hidden;
}
.service-card:last-child { border-right: none; }
.service-card::before {
  content: ''; position: absolute; bottom: 0; left: 0; right: 0;
  height: 3px; background: var(--gold);
  transform: scaleX(0); transform-origin: left; transition: transform 0.3s;
}
.service-card:hover { background: var(--off-white); }
.service-card:hover::before { transform: scaleX(1); }
.service-number { font-family: 'Barlow Condensed', sans-serif; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.2em; color: var(--aqua-light); margin-bottom: 1.25rem; }
.service-icon { width: 44px; height: 44px; border: 1.5px solid var(--aqua-light); display: flex; align-items: center; justify-content: center; color: var(--aqua-dark); font-size: 1.25rem; margin-bottom: 1.5rem; }
.service-card h3 { font-family: 'Cormorant Garamond', serif; font-size: 1.5rem; font-weight: 400; color: var(--dark); margin-bottom: 1rem; line-height: 1.3; }
.service-card p { font-size: 0.88rem; font-weight: 300; line-height: 1.75; color: var(--mid); margin-bottom: 1.5rem; }
.service-link { font-family: 'Barlow Condensed', sans-serif; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: var(--aqua-dark); text-decoration: none; display: flex; align-items: center; gap: 0.5rem; transition: color 0.2s, gap 0.2s; }
.service-link:hover { color: var(--gold); gap: 0.75rem; }
.faq-section { background: white; padding: 6rem 5rem; }
.faq-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; margin-top: 3rem; border: 1px solid rgba(141,189,189,0.2); }
.faq-item { padding: 2rem 2.5rem; border-right: 1px solid rgba(141,189,189,0.2); border-bottom: 1px solid rgba(141,189,189,0.2); transition: background 0.2s; }
.faq-item:nth-child(even) { border-right: none; }
.faq-item:hover { background: var(--off-white); }
.faq-q { font-family: 'Barlow', sans-serif; font-size: 0.9rem; font-weight: 500; color: var(--aqua-dark); margin-bottom: 0.75rem; display: flex; gap: 0.75rem; }
.faq-q::before { content: 'Q'; font-family: 'Barlow Condensed', sans-serif; font-size: 0.65rem; font-weight: 700; letter-spacing: 0.1em; color: var(--gold); flex-shrink: 0; margin-top: 2px; }
.faq-a { font-size: 0.85rem; font-weight: 300; line-height: 1.75; color: var(--mid); padding-left: 1.4rem; }
.team { background: var(--off-white); padding: 6rem 5rem; }
.team-inner { display: grid; grid-template-columns: 340px 1fr; gap: 5rem; align-items: center; margin-top: 3rem; }
.team-portraits { display: flex; gap: 1.5rem; }
.portrait-circle { width: 140px; height: 140px; border-radius: 50%; background: linear-gradient(135deg, var(--aqua-light), var(--aqua-dark)); border: 3px solid white; box-shadow: 0 4px 24px rgba(55,100,123,0.15); display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.5); font-family: 'Barlow Condensed', sans-serif; font-size: 0.6rem; letter-spacing: 0.1em; text-transform: uppercase; flex-shrink: 0; overflow: hidden; }
.portrait-circle img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
.portrait-circle:last-child { margin-top: 2.5rem; }
.team-bio h3 { font-family: 'Cormorant Garamond', serif; font-size: 2rem; font-weight: 300; color: var(--dark); margin-bottom: 1.5rem; line-height: 1.3; }
.team-bio p { font-size: 0.95rem; font-weight: 300; line-height: 1.85; color: var(--mid); margin-bottom: 1rem; }
.team-stats { display: flex; gap: 3rem; margin-top: 2rem; padding-top: 2rem; border-top: 1px solid rgba(141,189,189,0.3); }
.stat-number { font-family: 'Cormorant Garamond', serif; font-size: 2.5rem; font-weight: 300; color: var(--aqua-dark); line-height: 1; }
.stat-label { font-family: 'Barlow Condensed', sans-serif; font-size: 0.65rem; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: var(--mid); margin-top: 0.35rem; }
.contact-section { background: var(--aqua-dark); padding: 6rem 5rem; }
.contact-inner { display: grid; grid-template-columns: 1fr 1.2fr; gap: 5rem; align-items: start; }
.contact-left p { font-size: 0.9rem; font-weight: 300; line-height: 1.8; color: rgba(255,255,255,0.65); margin-bottom: 2rem; }
.contact-info-item { display: flex; align-items: flex-start; gap: 1rem; margin-bottom: 1.25rem; }
.contact-info-icon { width: 32px; height: 32px; border: 1px solid rgba(141,189,189,0.3); display: flex; align-items: center; justify-content: center; font-size: 0.85rem; color: var(--aqua-light); flex-shrink: 0; }
.contact-info-text { font-size: 0.85rem; color: rgba(255,255,255,0.7); line-height: 1.5; }
.contact-info-text strong { color: white; font-weight: 500; }
@media (max-width: 1200px) {
  .hero-left { padding: 4rem 3rem 4rem 4rem; }
  .hero-right { padding: 4rem 4rem 4rem 3rem; }
  .hero-statement { font-size: 2.4rem; }
  .hero-svg-logo { max-width: 322px; }
  .gallery-preview, .services, .faq-section, .team, .contact-section { padding: 5rem 4rem; }
  .team-inner { grid-template-columns: 280px 1fr; gap: 3.5rem; }
  .contact-inner { gap: 3.5rem; }
}
@media (max-width: 900px) {
  .hero { grid-template-columns: 1fr; }
  .hero::before { width: 100%; top: auto; height: 50%; bottom: 0; }
  .hero-left, .hero-right { padding: 3rem 2rem; }
  .hero-statement { font-size: 2rem; }
  .gallery-preview, .services, .faq-section, .team, .contact-section { padding: 4rem 2rem; }
  .gallery-grid { column-count: 2; }
  .services-grid, .faq-grid { grid-template-columns: 1fr; }
  .team-inner { grid-template-columns: 1fr; }
  .contact-inner { grid-template-columns: 1fr; }
}
@media (max-width: 600px) {
  .gallery-grid { column-count: 1; }
}
`;
